import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { AssociationType } from '../../core/SchemaClient';
import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import type {
  DetailField,
  EntityTypeConfig,
} from '../../record/registry/entityTypeRegistry';
import {
  DISPLAY_NAME_FIELDS,
  HIDDEN_ASSOCIATION_TARGETS,
  SYSTEM_FIELDS,
} from '../../record/registry/fieldConstants';
import {
  KINDS_RENDERED_WHOLE,
  resolvableTargetEntityTypes,
} from './associationTargetSubsets';
import {
  appendVisibleAssociations,
  buildFallbackList,
  isHiddenAssociation,
  passesReadKindFilter,
} from './buildFallbackFieldList';

// Re-export so existing consumers (tests, etc.) can still import from here
export { DISPLAY_NAME_FIELDS, HIDDEN_ASSOCIATION_TARGETS, SYSTEM_FIELDS };

/**
 * A rule that passed {@link isDecomposableRule}: its target kind is known to be
 * present, which is what lets callers read `target_entity_kind_name` as a
 * plain string instead of asserting it.
 */
export type DecomposableAssociationType = AssociationType & {
  target_entity_kind_name: string;
};

/**
 * A rule whose target is declared by KIND resolves to every entity type of that
 * kind, so it is rendered as one field per resolved type rather than one row
 * carrying every type under a single label.
 *
 * Only "many" rules are split: a rule holding a single target cannot sensibly
 * become N fields each claiming to hold it. (That is a limitation of expressing
 * the relationship through cardinality alone — see issue #12411.)
 */
export function isDecomposableRule(
  assocType: AssociationType | undefined
): assocType is DecomposableAssociationType {
  if (!assocType?.target_entity_kind_name) {
    return false;
  }
  // Some kinds are the user's own word for every type they cover, so splitting
  // them invents a distinction that isn't there (and the wildcard kind cannot
  // be enumerated at all). See KINDS_RENDERED_WHOLE.
  if (KINDS_RENDERED_WHOLE.has(assocType.target_entity_kind_name)) {
    return false;
  }
  return (
    assocType.cardinality === 'one_to_many' ||
    assocType.cardinality === 'many_to_many'
  );
}

/**
 * What a decomposable rule renders as, once its target kind is resolved against
 * the org's types.
 *
 * - `split` — two or more types to tell apart, so one field each.
 * - `whole` — a single resolvable type, so ONE field carrying the rule's own
 *   label and the whole-set write. Splitting here would rename the row for no
 *   benefit: there is nothing to tell apart, and the rule's label ("Employees",
 *   "Owned Deals") says more about the relationship than the type's plural
 *   label ("Contacts", "Deals") does. It would also route a write that never
 *   needed scoping through the scoped diff.
 * - `none` — every resolvable type is section-owned, so the rule contributes no
 *   field at all.
 */
export type AssociationFieldPlan =
  | { kind: 'split'; targetEntityTypeNames: string[] }
  | { kind: 'whole' }
  | { kind: 'none' };

/**
 * Decide how one decomposable rule is rendered — the SINGLE place that answers
 * it, so the read surface and the edit form cannot disagree.
 *
 * Note the asymmetry between the two counts: `whole` requires exactly one
 * *resolvable* type, not one *visible* type. A rule that resolves to a visible
 * type AND a section-owned one still has to split, because the undecomposed
 * field renders every target of the association type — including the hidden
 * one. Collapsing that case to `whole` would put the section-owned records back
 * under the rule's label, which is the bug decomposition exists to fix.
 */
export function planAssociationFields(
  assocType: DecomposableAssociationType,
  objectTypes: readonly { entity_type_name: string; entity_kind?: string[] }[]
): AssociationFieldPlan {
  const resolvable = resolvableTargetEntityTypes(
    assocType.target_entity_kind_name,
    objectTypes,
    assocType.target_excluded_entity_type_names ?? []
  );
  // A dedicated section already owns these types' records (a record's tasks
  // render in its Tasks section), so they never get a field here.
  // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
  const visible = resolvable.filter(t => !HIDDEN_ASSOCIATION_TARGETS.has(t));
  if (visible.length === 0) {
    return { kind: 'none' };
  }
  if (resolvable.length === 1) {
    return { kind: 'whole' };
  }
  return { kind: 'split', targetEntityTypeNames: visible };
}

/**
 * The per-type target names a decomposable rule expands into, or `null` when
 * it doesn't split (`whole` — a single resolvable type — or `none` — every
 * resolvable type is section-owned).
 *
 * The shared "resolve the plan, then read off `targetEntityTypeNames`" step
 * behind both `expandKindBasedAssociationFields` (the read/edit field-list
 * expansion) and the edit form's own per-type field derivation, so they
 * cannot drift on what counts as a split.
 */
export function decomposedTargetTypesFor(
  assocType: DecomposableAssociationType,
  objectTypes: readonly { entity_type_name: string; entity_kind?: string[] }[]
): string[] | null {
  const plan = planAssociationFields(assocType, objectTypes);
  return plan.kind === 'split' ? plan.targetEntityTypeNames : null;
}

export interface KindExpansionContext {
  /** association_type_name -> full AssociationType, for kind + cardinality. */
  assocTypeByName: ReadonlyMap<string, AssociationType>;
  /** The org's object types, for enumerating a kind's members. */
  objectTypes: readonly { entity_type_name: string; entity_kind?: string[] }[];
  /**
   * Target types this record actually has rows of, per association type.
   *
   * Read mode passes this so an empty subset renders no field; edit mode omits
   * it so every resolvable type gets a field to add the first target through.
   */
  presentTargetTypesByAssoc?: ReadonlyMap<string, ReadonlySet<string>>;
}

/**
 * Expand each kind-based association field into one field per resolved target
 * type, dropping the types a dedicated section owns.
 *
 * Applied to the finished list so both the ordered path and the fallback path
 * — and therefore both the read surface and the edit form — inherit it from one
 * place and cannot drift.
 */
export function expandKindBasedAssociationFields(
  fields: readonly DetailField[],
  ctx: KindExpansionContext
): DetailField[] {
  const result: DetailField[] = [];
  for (const field of fields) {
    const assocType =
      field.type === 'association'
        ? ctx.assocTypeByName.get(field.key)
        : undefined;
    if (field.type !== 'association' || !isDecomposableRule(assocType)) {
      result.push(field);
      continue;
    }
    const targetEntityTypeNames = decomposedTargetTypesFor(
      assocType,
      ctx.objectTypes
    );
    if (targetEntityTypeNames === null) {
      // Not split — distinguish `whole` (keep the rule's own undecomposed
      // field) from `none` (the rule contributes nothing) the same way
      // decomposedTargetTypesFor does internally.
      if (planAssociationFields(assocType, ctx.objectTypes).kind === 'whole') {
        result.push(field);
      }
      continue;
    }
    const present = ctx.presentTargetTypesByAssoc?.get(field.key);
    for (const targetEntityTypeName of targetEntityTypeNames) {
      if (present && !present.has(targetEntityTypeName)) {
        continue;
      }
      result.push({
        type: 'association',
        key: field.key,
        targetEntityTypeName,
      });
    }
  }
  return result;
}

export function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Schema/config-level visibility filter for a single property key — the ONE
 * implementation of the property visibility rules, shared by the ordered path
 * below, the fallback path (buildFallbackFieldList.ts), and the merge
 * candidate cards (getMergeCardPropertyKeys.ts) so the rules can never drift
 * (parity with the exported association carve-outs in
 * buildFallbackFieldList.ts).
 *
 * Deliberately excludes the populated/empty-value check: the record detail
 * view checks emptiness via getPropertyValue() + isEmptyValue, while the merge
 * cards check raw `.value` via isMergeValueEmpty (documented parity with
 * CardPropertyRow) — that per-caller check stays at each call site.
 */
export function isVisiblePropertyKey(
  key: string,
  mode: 'read' | 'edit',
  hiddenSet: ReadonlySet<string>,
  schema: PropertySchemaMap
): boolean {
  if (SYSTEM_FIELDS.has(key)) {
    return false;
  }
  if (mode === 'read' && DISPLAY_NAME_FIELDS.has(key)) {
    return false;
  }
  if (hiddenSet.has(key)) {
    return false;
  }
  const propSchema = schema[key];
  if (!propSchema) {
    return false;
  }
  if (propSchema.value_read_access !== 'all') {
    return false;
  }
  // edit mode: skip non-writable properties (formulas, rollups, etc.)
  if (mode === 'edit' && propSchema.value_write_access !== 'all') {
    return false;
  }
  return true;
}

type ReadOptions = {
  mode: 'read';
  config: EntityTypeConfig;
  schema: PropertySchemaMap;
  object: CrmObject;
  assocAccessMap: Map<string, string>;
  /** Map from association_type_name → target_entity_type_name for filtering hidden targets */
  associationTargetMap?: Map<string, string>;
  /** entity_type_name -> entity_kind[] (from buildEntityKindMap). */
  entityKindMap: ReadonlyMap<string, readonly string[]>;
  /** association_type_name -> full AssociationType, for kind resolution. */
  assocTypeByName: Map<string, AssociationType>;
  /**
   * Property keys kept in the list even when their value is empty, in their
   * normal position. Read mode otherwise drops empty properties; the overview
   * passes the create form's fields (rendered as click-to-fill placeholders)
   * and any field the user cleared during this visit (so the row stays
   * under the cursor).
   */
  keepEmptyPropertyKeys?: ReadonlySet<string>;
  /**
   * Association type names kept in the list even when the object has no
   * target for them. Read mode otherwise lists only the associations present
   * on the object (plus those in `detailFieldOrder`), so a create-form
   * association with no target yet — or one the user just cleared — has to
   * be asked for explicitly. Subject to the same access / hidden-target /
   * kind filters as every other association row.
   */
  keepEmptyAssociationKeys?: ReadonlySet<string>;
};

/** Whether a read-mode property row should be dropped for having no value. */
export function dropsEmptyProperty(
  key: string,
  options: Pick<ReadOptions, 'object' | 'keepEmptyPropertyKeys'>
): boolean {
  if (options.keepEmptyPropertyKeys?.has(key)) {
    return false;
  }
  const value = getPropertyValue<unknown>(options.object.properties?.[key]);
  return isEmptyValue(value);
}

type EditOptions = {
  mode: 'edit';
  config: EntityTypeConfig;
  schema: PropertySchemaMap;
  editableAssocTypeNames: Set<string>;
  editableAssociationTypes: Array<{ association_type_name: string }>;
};

export type BuildDetailFieldListOptions = ReadOptions | EditOptions;

/**
 * Builds an ordered list of DetailFields for either read or edit mode,
 * respecting detailFieldOrder config, hidden properties, access controls,
 * HIDDEN_ASSOCIATION_TARGETS, and mode-specific rules (empty-value
 * filtering, write-access, timestamps).
 */
export function buildDetailFieldList(
  options: BuildDetailFieldListOptions
): DetailField[] {
  const { mode, config, schema } = options;
  const { detailFieldOrder, hiddenProperties = [] } = config;
  const hiddenSet = new Set(hiddenProperties);
  const hiddenAssocSet = new Set(config.hiddenAssociations ?? []);

  if (detailFieldOrder) {
    const orderedPropertyKeys = new Set<string>();
    const orderedAssocKeys = new Set<string>();
    const result: DetailField[] = [];
    // Collect timestamps separately so they always render last,
    // even when remaining (unordered) schema properties are appended.
    const timestamps: DetailField[] = [];

    for (const field of detailFieldOrder) {
      if (field.type === 'property') {
        orderedPropertyKeys.add(field.key);
        if (!isVisiblePropertyKey(field.key, mode, hiddenSet, schema)) {
          continue;
        }
        if (mode === 'read' && dropsEmptyProperty(field.key, options)) {
          continue;
        }
        result.push(field);
      } else if (field.type === 'association') {
        orderedAssocKeys.add(field.key);
        // Registry-hidden associations never render, even when enumerated in
        // detailFieldOrder (parity with appendVisibleAssociations).
        if (hiddenAssocSet.has(field.key)) {
          continue;
        }
        if (mode === 'read') {
          if (options.assocAccessMap.get(field.key) === 'internal') {
            continue;
          }
          // Skip hidden targets (notes, files, meetings, etc.)
          if (isHiddenAssociation(field.key, options.associationTargetMap)) {
            continue;
          }
          if (!passesReadKindFilter(field.key, options)) {
            continue;
          }
          result.push(field);
        } else {
          // edit mode — already filtered by editableAssocTypeNames from caller
          if (!options.editableAssocTypeNames.has(field.key)) {
            continue;
          }
          result.push(field);
        }
      } else if (field.type === 'timestamp') {
        if (mode === 'read') {
          const value = getPropertyValue<unknown>(
            options.object.properties?.[field.key]
          );
          if (!value) {
            continue;
          }
          timestamps.push(field);
        }
        // edit mode: skip timestamp fields entirely (read-only system fields)
      }
    }

    // Append remaining schema properties not in the ordered list
    for (const key of Object.keys(schema)) {
      if (orderedPropertyKeys.has(key)) {
        continue;
      }
      if (!isVisiblePropertyKey(key, mode, hiddenSet, schema)) {
        continue;
      }
      if (mode === 'read' && dropsEmptyProperty(key, options)) {
        continue;
      }
      result.push({ type: 'property', key });
    }

    // Append associations present on the object but not enumerated in
    // detailFieldOrder — detailFieldOrder is an ordering hint for associations,
    // not a visibility allowlist (parity with edit mode + the fallback path).
    if (mode === 'read') {
      result.push(...appendVisibleAssociations(options, orderedAssocKeys));
    }

    // Timestamps always render last, after any remaining properties
    result.push(...timestamps);

    return result;
  }

  return buildFallbackList(options);
}

/**
 * Minimum number of overview rows shown inline before anything is collapsed
 * behind the "See more" disclosure. Acts as a floor: a record with fewer than
 * this many visible fields shows all of them inline (no toggle). Past the
 * floor, primary fields always render inline and secondary fields are pulled up
 * only to reach the floor — the remainder collapse.
 */
export const MIN_INLINE_OVERVIEW_FIELDS = 10;

/**
 * Partition the visible overview fields into the rows shown inline and the rows
 * collapsed behind "See more".
 *
 * Rules (see OverviewSection):
 *  - All `primary` fields (those for which `isSecondary` is false) always render
 *    inline, in order.
 *  - `secondary` fields are pulled up to fill the inline set until it reaches
 *    `minInline` rows. Whatever secondary fields are left over collapse.
 *  - When `collapsedSecondary` is empty, the caller renders no toggle.
 *
 * Pure and order-preserving so it can be unit-tested independently of the
 * component's hook wiring.
 */
export function partitionOverviewFields(
  visibleFields: DetailField[],
  isSecondary: (field: DetailField) => boolean,
  minInline: number = MIN_INLINE_OVERVIEW_FIELDS
): {
  primary: DetailField[];
  inlineSecondary: DetailField[];
  collapsedSecondary: DetailField[];
} {
  const primary = visibleFields.filter(field => !isSecondary(field));
  const secondary = visibleFields.filter(isSecondary);
  // How many secondary rows to promote inline so the inline set reaches the
  // floor. Never negative (primary already past the floor → promote none).
  const promote = Math.max(0, minInline - primary.length);
  return {
    primary,
    inlineSecondary: secondary.slice(0, promote),
    collapsedSecondary: secondary.slice(promote),
  };
}

/**
 * Partition EDIT-mode fields into the main list and an Advanced group.
 *
 * Order-preserving and pure so it is unit-testable independently.
 */
export function partitionAdvancedFields(
  fields: DetailField[],
  advancedKeys: ReadonlySet<string>
): { main: DetailField[]; advanced: DetailField[] } {
  const advanced = fields.filter(
    f => f.type === 'property' && advancedKeys.has(f.key)
  );
  const main = fields.filter(
    f => !(f.type === 'property' && advancedKeys.has(f.key))
  );
  return { main, advanced };
}
